import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BridgeClubLayout from '../../components/BridgeClub/BridgeClubLayout';
import { useBridgeClubAuth } from '../../contexts/BridgeClubAuthContext';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';
import './BridgeClubStoreFinder.css';

interface Store {
  id: number;
  name: string;
  city: string;
  state: string;
  zip?: string;
  phone?: string;
  address?: string;
}

const BridgeClubStoreFinder: React.FC = () => {
  const navigate = useNavigate();
  const { member, refreshMember } = useBridgeClubAuth();

  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [searchState, setSearchState] = useState(member?.state || 'WA');
  const [searchCity, setSearchCity] = useState('');

  // Helper functions for directions and calling
  const getDirectionsUrl = (store: Store) => {
    const addressParts = [store.address, store.city, store.state, store.zip].filter(Boolean);
    const address = addressParts.join(', ');
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
  };

  const getCallUrl = (phone: string) => {
    return `tel:${phone.replace(/[^0-9+]/g, '')}`;
  };

  useEffect(() => {
    loadStores();
  }, []);

  const loadStores = async (state?: string, city?: string) => {
    setLoading(true);
    try {
      const data = await bridgeClubAPI.getStores({
        state: state || searchState,
        city: city || undefined,
      });
      setStores(data);
    } catch (err) {
      console.error('Failed to load stores:', err);
      setError('Failed to load stores');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadStores(searchState, searchCity);
  };

  const handleSelectStore = async (store: Store) => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await bridgeClubAPI.linkStore(store.id);
      await refreshMember();
      setSuccess(`You're now linked to ${store.name}!`);
      setTimeout(() => {
        navigate('/bridge-club/profile');
      }, 1500);
    } catch (err: any) {
      setError(err?.detail || 'Failed to link store');
    } finally {
      setSaving(false);
    }
  };

  return (
    <BridgeClubLayout>
      <div className="store-finder-page">
        <h1>Find Your Store</h1>
        <p className="page-subtitle">Choose your home store to earn and redeem rewards</p>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {/* Search */}
        <div className="search-form">
          <div className="search-row">
            <select
              value={searchState}
              onChange={(e) => setSearchState(e.target.value)}
              className="state-select"
            >
              <option value="WA">Washington</option>
              <option value="ID">Idaho</option>
              <option value="OR">Oregon</option>
              <option value="MT">Montana</option>
            </select>
            <input
              type="text"
              value={searchCity}
              onChange={(e) => setSearchCity(e.target.value)}
              placeholder="City (optional)"
              className="city-input"
            />
            <button onClick={handleSearch} className="search-btn">
              Search
            </button>
          </div>
        </div>

        {/* Current Store */}
        {member?.linked_store && (
          <div className="current-store">
            <span className="current-label">Current Store</span>
            <div className="current-store-info">
              <span className="store-name">{member.linked_store.name}</span>
              <span className="store-location">
                {member.linked_store.city}, {member.linked_store.state}
              </span>
            </div>
          </div>
        )}

        {/* Store List */}
        {loading ? (
          <div className="loading-stores">
            <div className="bridge-club-spinner"></div>
            <p>Finding stores...</p>
          </div>
        ) : stores.length === 0 ? (
          <div className="no-stores">
            <p>No stores found in this area.</p>
            <p>Try searching in a different location.</p>
          </div>
        ) : (
          <div className="stores-list">
            <h3>{stores.length} stores found</h3>
            {stores.map((store) => {
              const isLinked = member?.linked_store?.id === store.id;
              return (
                <div key={store.id} className={`store-card ${isLinked ? 'linked' : ''}`}>
                  <div className="store-info">
                    <span className="store-name">{store.name}</span>
                    <span className="store-location">{store.city}, {store.state}</span>
                    {store.address && (
                      <span className="store-address">{store.address}</span>
                    )}
                    {store.phone && (
                      <span className="store-phone">{store.phone}</span>
                    )}
                  </div>
                  <div className="store-actions">
                    {store.address && (
                      <a
                        href={getDirectionsUrl(store)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="action-btn directions-btn"
                      >
                        Directions
                      </a>
                    )}
                    {store.phone && (
                      <a href={getCallUrl(store.phone)} className="action-btn call-btn">
                        Call
                      </a>
                    )}
                    {isLinked ? (
                      <div className="linked-badge">✓ Linked</div>
                    ) : (
                      <button
                        onClick={() => handleSelectStore(store)}
                        disabled={saving}
                        className="select-btn"
                      >
                        {saving ? '...' : 'Select'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </BridgeClubLayout>
  );
};

export default BridgeClubStoreFinder;
